/**
 * @file device.ts
 * @description VT2 ULTRA V2 的 Telink 3228C 设备入口。
 */

import type { HidChannel } from '../../../channels/hid/HidChannel'
import { InputReportExchange } from '../../../channels/hid/exchange/InputReportExchange'
import { Telink3228CMouseBattery } from '../../../chips/telink/feature-impls/mouse/battery'
import { Telink3228CMouseDPI } from '../../../chips/telink/feature-impls/mouse/dpi'
import { Telink3228CMouseConfig } from '../../../chips/telink/feature-impls/mouse/config'
import { Telink3228CMouseFirmware } from '../../../chips/telink/feature-impls/mouse/firmware'
import { Telink3228CMouseKeys } from '../../../chips/telink/feature-impls/mouse/mouse-keys'
import { Telink3228CMousePairing } from '../../../chips/telink/feature-impls/mouse/pairing'
import { Telink3228CMousePerfCfg } from '../../../chips/telink/feature-impls/mouse/mouse-performance'
import {
  createVisionApPairingPayload,
  createVisionApPayload,
  getVisionApProfileBaseAddress,
  isVisionApFunctionSynchronizationReport,
  isVisionApProfileChangeReport,
  isVisionApCommandEcho,
  isVisionApResponseForRequest,
  parseVisionApBatteryRuntime,
  parseVisionApResponse,
  type VisionApFunctionAreaSession,
  type VisionApKeyAreaSession,
  type VisionApMacroAreaSession,
  type VisionApMacroNameAreaSession,
  type VisionApProfileNameAreaSession,
  type VisionApResponse,
  VISION_AP_MOUSE_BATTERY_RUNTIME,
  VISION_AP_MOUSE_COMMAND,
  VISION_AP_MOUSE_FUNCTION_AREA,
  VISION_AP_MOUSE_HID,
  VISION_AP_MOUSE_MACRO_NAME_AREA,
  VISION_AP_MOUSE_PROFILE_NAME_AREA,
} from '../../../chips/telink/protocols/vision-ap-mouse'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { NoticeEvents } from '../../../events/NoticeEvents'
import { ReportEvents } from '../../../events/ReportEvents'
import type { BatteryRuntime, BatteryStatus } from '../../../features/battery/contract'
import { BaseDevice } from '../../../runtime/BaseDevice'
import type { mouseUserCustom } from '../../../types/config'
import type { DeviceManifest } from '../../../types/manifest'

const FUNCTION_AREA_SNAPSHOT_LENGTH = VISION_AP_MOUSE_FUNCTION_AREA.byteLength
// 文档允许 DATA 最大 56 字节，但接收器实测大帧透传会截断后段字段；直连和接收器统一为已验证的安全粒度。
// 功能区必须完整读改写，尤其要保留尚未建模的尾部字段，不能在写入时自行补值。
const FUNCTION_AREA_CHUNK_LENGTHS = [24, 24, 24, 24, 24, 8] as const
// V0106 profile 名称区同为 128 字节单区；BB/BC 无实机样本，沿用功能区已验证的安全分包，不自行放宽。
const PROFILE_NAME_AREA_CHUNK_LENGTHS: readonly number[] = FUNCTION_AREA_CHUNK_LENGTHS
// V0106 宏名称区 240 字节单区；B9/BA 同样按 24 字节安全分包，恰好 10 包。
const MACRO_NAME_AREA_CHUNK_LENGTHS: readonly number[] = Array.from(
  { length: VISION_AP_MOUSE_MACRO_NAME_AREA.byteLength / 24 },
  () => 24,
)
const CONNECTION_TYPE_WIRED = 0xff
const CONNECTION_TYPE_WIRELESS = 0xa5
const FACTORY_RESET_PROCESSING_TIME_MS = 2000
const WAKE_PROBE_TIMEOUT_MS = 500
const WAKE_RETRY_DELAY_MS = 100
const COMMAND_RESPONSE_TIMEOUT_MS = 3000
// 官方驱动 AD 请求帧固定 LEN=6、ADDR=0；回包 DATA[0]=电量、DATA[1]=充电状态，其余字节为填充。
const BATTERY_QUERY_DATA_LENGTH = 6
const BATTERY_LEVEL_MAX = 100
const BATTERY_STATUS_VALID = 1
const BATTERY_STATUS_CHARGING = 2

const WAKE_RETRYABLE_COMMANDS = new Set<number>([
  VISION_AP_MOUSE_COMMAND.getFunctionArea,
  VISION_AP_MOUSE_COMMAND.getCurrentKeyArea,
  VISION_AP_MOUSE_COMMAND.getMacroArea,
  VISION_AP_MOUSE_COMMAND.getBatteryLevel,
  VISION_AP_MOUSE_COMMAND.readBatteryRuntime,
  VISION_AP_MOUSE_COMMAND.readMacroName,
  VISION_AP_MOUSE_COMMAND.readProfileName,
])

const VISION_AP_READ_COMMANDS = new Set<number>(WAKE_RETRYABLE_COMMANDS)

interface FunctionAreaMutation {
  update: (area: Uint8Array) => void
  resolve: () => void
  reject: (error: unknown) => void
}

/** VT2 ULTRA V2 的功能变量区传输装配；字段语义由各 Feature 自行解释。 */
export class Telink3228CMouseDevice extends BaseDevice {
  private readonly configFeature: Telink3228CMouseConfig
  private readonly dpiFeature: Telink3228CMouseDPI
  private readonly mouseKeysFeature: Telink3228CMouseKeys
  private readonly mousePerfCfgFeature: Telink3228CMousePerfCfg
  private functionStateRefreshInFlight?: Promise<void>
  private functionStateRefreshQueued = false
  private profileRefreshInFlight?: Promise<void>
  private profileRefreshQueued = false
  /** 按 profile 隔离的功能区待写变更；在设备层统一服务 DPI 与参数 feature。 */
  private readonly functionAreaMutationQueues = new Map<number, FunctionAreaMutation[]>()
  private readonly functionAreaMutationDrains = new Set<number>()

  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)
    this.connectionType = this.isWirelessConnection(channel)
      ? CONNECTION_TYPE_WIRELESS
      : CONNECTION_TYPE_WIRED
    this.features.battery = new Telink3228CMouseBattery(this)
    this.configFeature = new Telink3228CMouseConfig(this)
    this.features.config = this.configFeature
    this.dpiFeature = new Telink3228CMouseDPI(this)
    this.features.dpi = this.dpiFeature
    this.features.firmware = new Telink3228CMouseFirmware(this)
    this.mouseKeysFeature = new Telink3228CMouseKeys(this)
    this.features.mouseKeys = this.mouseKeysFeature
    this.mousePerfCfgFeature = new Telink3228CMousePerfCfg(this)
    this.features.mousePerfCfg = this.mousePerfCfgFeature
    this.features.pairing = new Telink3228CMousePairing(this)
    this.currentConfig = { index: 0, name: '默认配置' }
    this.configList = Array.from(
      { length: VISION_AP_MOUSE_FUNCTION_AREA.profileCount },
      (_, index) => ({ index, name: index === 0 ? '默认配置' : `配置 ${index + 1}` }),
    )
  }

  public override async open(): Promise<void> {
    await super.open()
    await this.primeBatterySnapshotOnOpen()
  }

  protected override handleReport(reportId: number, data: DataView): void {
    super.handleReport(reportId, data)
    if (isVisionApProfileChangeReport(reportId, data)) {
      void this.refreshCurrentProfile()
      return
    }
    if (isVisionApFunctionSynchronizationReport(reportId, data)) {
      void this.refreshFunctionState()
    }
  }

  public async runFunctionAreaSession<T>(
    operation: (session: VisionApFunctionAreaSession) => Promise<T>,
    profileIndex = this.currentConfig.index,
  ): Promise<T> {
    const profileBaseAddress = getVisionApProfileBaseAddress(profileIndex)
    // 厂商确认（2026-08-18）VT2 ULTRA V2 链路不需要 A1/A2，有线与接收器统一直发 A4/A5。
    return this.runAtomic(async (channel) =>
      operation({
        readFunctionAreaSnapshot: () => this.readFunctionAreaSnapshot(channel, profileBaseAddress),
        writeFunctionAreaSnapshot: (area) =>
          this.writeFunctionAreaSnapshot(channel, area, profileBaseAddress),
      }),
    )
  }

  /**
   * 合并同一 profile 在慢速 A4/A5 分包链路上的连续字段更新。
   *
   * 首个更新立即开始一次完整读+回写；传输期间到达的后续更新会合并到下一份
   * 最新快照中，避免每次步进都重复传输 12 个分包，同时保持各调用仅在其值真正
   * 写入设备后才完成。
   */
  public updateFunctionArea(
    update: (area: Uint8Array) => void,
    profileIndex = this.currentConfig.index,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const queue = this.functionAreaMutationQueues.get(profileIndex) ?? []
      queue.push({ update, resolve, reject })
      this.functionAreaMutationQueues.set(profileIndex, queue)
      void this.drainFunctionAreaMutations(profileIndex)
    })
  }

  public async runKeyAreaSession<T>(
    operation: (session: VisionApKeyAreaSession) => Promise<T>,
    profileIndex = this.currentConfig.index,
  ): Promise<T> {
    const profileBaseAddress = getVisionApProfileBaseAddress(profileIndex)
    return this.runAtomic(async (channel) =>
      operation({
        readKeyArea: (address, length) =>
          this.readKeyArea(channel, profileBaseAddress + address, length),
        writeKeyArea: (address, data) =>
          this.writeKeyArea(channel, profileBaseAddress + address, data),
      }),
    )
  }

  /** 宏区 AA/AB 为直发命令（原厂确认，2026-08-17）。 */
  public async runMacroAreaSession<T>(
    operation: (session: VisionApMacroAreaSession) => Promise<T>,
  ): Promise<T> {
    return this.runAtomic(async (channel) =>
      operation({
        readMacroArea: (address, length) => this.readMacroArea(channel, address, length),
        writeMacroArea: (address, data) => this.writeMacroArea(channel, address, data),
      }),
    )
  }

  /**
   * V0106 profile 名称区 BB/BC：全局单区 128 字节，ADDR 从 0 起按名称区内偏移寻址。
   * 协议要求写入必须发满 128 字节，因此只暴露整区读改写，由 config feature 负责保留其它槽位。
   */
  public async runProfileNameAreaSession<T>(
    operation: (session: VisionApProfileNameAreaSession) => Promise<T>,
  ): Promise<T> {
    return this.runAtomic(async (channel) =>
      operation({
        readProfileNameArea: () =>
          this.readNameArea(
            channel,
            VISION_AP_MOUSE_COMMAND.readProfileName,
            PROFILE_NAME_AREA_CHUNK_LENGTHS,
            VISION_AP_MOUSE_PROFILE_NAME_AREA.byteLength,
            'profile 名称区',
          ),
        writeProfileNameArea: (area) =>
          this.writeNameArea(
            channel,
            VISION_AP_MOUSE_COMMAND.writeProfileName,
            PROFILE_NAME_AREA_CHUNK_LENGTHS,
            VISION_AP_MOUSE_PROFILE_NAME_AREA.byteLength,
            area,
            'profile 名称区',
          ),
      }),
    )
  }

  /**
   * V0106 宏名称区 B9/BA：全局单区 240 字节，ADDR 从 0 起按名称区内偏移寻址。
   * 与宏区 AA/AB 相互独立；协议要求写入必须发满 240 字节，因此只暴露整区读改写。
   */
  public async runMacroNameAreaSession<T>(
    operation: (session: VisionApMacroNameAreaSession) => Promise<T>,
  ): Promise<T> {
    return this.runAtomic(async (channel) =>
      operation({
        readMacroNameArea: () =>
          this.readNameArea(
            channel,
            VISION_AP_MOUSE_COMMAND.readMacroName,
            MACRO_NAME_AREA_CHUNK_LENGTHS,
            VISION_AP_MOUSE_MACRO_NAME_AREA.byteLength,
            '宏名称区',
          ),
        writeMacroNameArea: (area) =>
          this.writeNameArea(
            channel,
            VISION_AP_MOUSE_COMMAND.writeMacroName,
            MACRO_NAME_AREA_CHUNK_LENGTHS,
            VISION_AP_MOUSE_MACRO_NAME_AREA.byteLength,
            area,
            '宏名称区',
          ),
      }),
    )
  }

  /**
   * AD 电量查询：供电量 feature 显式调用，也用于连接完成后的首页电量快照预热。
   * 回包 DATA[0] 为 0-100 电量，DATA[1] 为充电状态（0=未充电、1=充电），
   * 超出协议定义范围的值按读取失败处理，不猜测语义。
   */
  public async readBatteryStatus(): Promise<BatteryStatus> {
    return this.runAtomic(async (channel) => {
      const response = await this.requestCommand(
        channel,
        VISION_AP_MOUSE_COMMAND.getBatteryLevel,
        BATTERY_QUERY_DATA_LENGTH,
        0,
      )
      this.assertResponseLocation(response, 0, BATTERY_QUERY_DATA_LENGTH, '读取电量')
      const level = response.data[0]
      const chargingByte = response.data[1]
      if (level > BATTERY_LEVEL_MAX) {
        throw new DeviceError(
          DeviceErrorCode.READ_FAILED,
          `电量读取值超出 0-${BATTERY_LEVEL_MAX} 范围: ${level}`,
        )
      }
      if (chargingByte !== 0 && chargingByte !== 1) {
        throw new DeviceError(
          DeviceErrorCode.READ_FAILED,
          `充电状态读取到未定义值: 0x${chargingByte.toString(16)}`,
        )
      }
      return { level, charging: chargingByte === 1 }
    })
  }

  /**
   * BD 续航查询：LEN=4、ADDR=0，DATA 为大端最低/最高续航小时。
   * 只把设备返回的小时数交给 Web 展示，不据此推算剩余电量或休眠策略。
   */
  public async readBatteryRuntime(): Promise<BatteryRuntime> {
    return this.runAtomic(async (channel) => {
      const response = await this.requestCommand(
        channel,
        VISION_AP_MOUSE_COMMAND.readBatteryRuntime,
        VISION_AP_MOUSE_BATTERY_RUNTIME.dataLength,
        VISION_AP_MOUSE_BATTERY_RUNTIME.address,
      )
      this.assertResponseLocation(
        response,
        VISION_AP_MOUSE_BATTERY_RUNTIME.address,
        VISION_AP_MOUSE_BATTERY_RUNTIME.dataLength,
        '读取续航时间',
      )
      return parseVisionApBatteryRuntime(response.data)
    })
  }

  /**
   * A9 触发后设备约两秒不响应；等待必须保留在原子队列内，避免页面跳转时后续读命令误判超时。
   */
  public async restoreFactorySettings(): Promise<void> {
    await this.runAtomic(async (channel) => {
      await this.sendCommandWithoutResponse(channel, VISION_AP_MOUSE_COMMAND.restoreFactorySettings)
      await delay(FACTORY_RESET_PROCESSING_TIME_MS)
    })
    // 协议未说明 A9 是否重置名称区；丢弃会话缓存，下次读取时以设备为准。
    this.mouseKeysFeature.invalidateMacroNameArea()
  }

  public async checkConnect(): Promise<boolean> {
    if (!this.channel.isConnected) return false
    return this.runAtomic((channel) => this.readFunctionAreaProbe(channel))
  }

  /**
   * 有线鼠标进配对：V0104 `0xB1`，will not reply。
   * Qt/验证包帧为 `04 00 00 B1`（Cs 固定 0）；窗 60s。
   */
  public async enterMousePairingMode(): Promise<void> {
    if (this.connectionType === CONNECTION_TYPE_WIRELESS) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, '鼠标进配对仅支持在 USB 有线鼠标上执行')
    }
    return this.runAtomic((channel) =>
      this.sendPairingCommandWithoutResponse(channel, VISION_AP_MOUSE_COMMAND.enterMousePairing),
    )
  }

  /**
   * 接收器进配对：V0104 `0x92`，will not reply。
   * Qt/验证包帧为 `04 00 00 92`；结果在接收器 Consumer Control `0x0C:0x01` 上听 `03 FF …`。
   */
  public async enterDonglePairingMode(): Promise<void> {
    if (this.connectionType !== CONNECTION_TYPE_WIRELESS) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, '接收器进配对仅支持在 USB 接收器上执行')
    }
    return this.runAtomic((channel) =>
      this.sendPairingCommandWithoutResponse(channel, VISION_AP_MOUSE_COMMAND.enterDonglePairing),
    )
  }

  private async readFunctionAreaProbe(channel: HidChannel): Promise<boolean> {
    try {
      await this.readFunctionArea(channel, 0, FUNCTION_AREA_CHUNK_LENGTHS[0])
      return true
    } catch {
      return false
    }
  }

  private async primeBatterySnapshotOnOpen(): Promise<void> {
    try {
      const status = await this.readBatteryStatus()
      const current = (this.userCustomData as Partial<mouseUserCustom> | undefined) ?? {}
      this.userCustomData = {
        ...current,
        conProtocol: current.conProtocol ?? this.getDefaultConnectionProtocol(),
        sensor: current.sensor ?? 0,
        cpiLev: current.cpiLev ?? 0,
        xCpiVal: current.xCpiVal ?? 0,
        yCpiVal: current.yCpiVal ?? 0,
        batStatus: status.charging ? BATTERY_STATUS_CHARGING : BATTERY_STATUS_VALID,
        batPercent: status.level,
      }
    } catch (error) {
      console.warn('[Telink3228CMouseDevice] Failed to prime battery snapshot on open:', error)
    }
  }

  /** V0107 的 03 05 04 不携带索引，收到后必须回读 profile 0 的 byte 0。 */
  private refreshCurrentProfile(): Promise<void> {
    if (this.profileRefreshInFlight) {
      this.profileRefreshQueued = true
      return this.profileRefreshInFlight
    }

    const refresh = this.readAndPublishCurrentProfile()
      .catch((error) => {
        console.warn('[Telink3228CMouseDevice] Failed to synchronize current profile:', error)
      })
      .finally(() => {
        this.profileRefreshInFlight = undefined
        if (this.profileRefreshQueued) {
          this.profileRefreshQueued = false
          void this.refreshCurrentProfile()
        }
      })
    this.profileRefreshInFlight = refresh
    return refresh
  }

  private async readAndPublishCurrentProfile(): Promise<void> {
    const index = await this.configFeature.getCurrentConfigIndex()
    if (this.currentConfig.index === index) return

    let config = this.configList.find((item) => item.index === index)
    if (!config) {
      // 固件若切到尚未命名的固定槽，仍需先同步真实当前配置；该槽随后会作为当前项显示。
      config = { index, name: await this.configFeature.getCurrentConfigName(index) }
      this.configList = [...this.configList, config].sort((left, right) => left.index - right.index)
    }

    this.currentConfig = config
    const current = (this.userCustomData as Partial<mouseUserCustom> | undefined) ?? {}
    this.userCustomData = {
      ...current,
      conProtocol: current.conProtocol ?? this.getDefaultConnectionProtocol(),
      sensor: current.sensor ?? 0,
      cpiLev: current.cpiLev ?? 0,
      xCpiVal: current.xCpiVal ?? 0,
      yCpiVal: current.yCpiVal ?? 0,
      batStatus: current.batStatus ?? 0,
      batPercent: current.batPercent ?? 0,
      cfgSel: index,
    }
    this.emit(ReportEvents.CfgChanged, this.userCustomData)
    this.emit(NoticeEvents.CfgChanged, config)
  }

  private refreshFunctionState(): Promise<void> {
    if (this.functionStateRefreshInFlight) {
      this.functionStateRefreshQueued = true
      return this.functionStateRefreshInFlight
    }

    const refresh = this.readAndPublishFunctionState()
      .catch((error) => {
        console.warn('[Telink3228CMouseDevice] Failed to synchronize function state:', error)
      })
      .finally(() => {
        this.functionStateRefreshInFlight = undefined
        if (this.functionStateRefreshQueued) {
          this.functionStateRefreshQueued = false
          void this.refreshFunctionState()
        }
      })
    this.functionStateRefreshInFlight = refresh
    return refresh
  }

  private async readAndPublishFunctionState(): Promise<void> {
    const profileIndex = this.currentConfig.index
    const { dpiSnapshot, reportRateSnapshot } = await this.runFunctionAreaSession(
      async (session) => {
        const area = await session.readFunctionAreaSnapshot()
        return {
          dpiSnapshot: this.dpiFeature.parseCurrentGearSnapshot(area),
          reportRateSnapshot: this.mousePerfCfgFeature.parseCurrentReportRateSnapshot(area),
        }
      },
      profileIndex,
    )
    if (this.currentConfig.index !== profileIndex) return

    const current = (this.userCustomData as Partial<mouseUserCustom> | undefined) ?? {}
    this.userCustomData = {
      ...current,
      conProtocol: current.conProtocol ?? this.getDefaultConnectionProtocol(),
      sensor: current.sensor ?? 0,
      batStatus: current.batStatus ?? 0,
      batPercent: current.batPercent ?? 0,
      ...dpiSnapshot,
      rpt: reportRateSnapshot.reportRate,
      sensorMode: reportRateSnapshot.sensorMode,
    }
    this.emit(ReportEvents.DpiGearChanged, dpiSnapshot)
    this.emit(ReportEvents.ReportRateChanged, reportRateSnapshot)
  }

  private getDefaultConnectionProtocol(): mouseUserCustom['conProtocol'] {
    return this.connectionType === CONNECTION_TYPE_WIRELESS ? 0 : 2
  }

  private async readFunctionAreaSnapshot(
    channel: HidChannel,
    profileBaseAddress: number,
  ): Promise<Uint8Array> {
    const area = new Uint8Array(FUNCTION_AREA_SNAPSHOT_LENGTH)
    await this.readFunctionAreaSnapshotInChunks(channel, area, profileBaseAddress)
    return area
  }

  private async drainFunctionAreaMutations(profileIndex: number): Promise<void> {
    if (this.functionAreaMutationDrains.has(profileIndex)) return
    this.functionAreaMutationDrains.add(profileIndex)

    try {
      while (true) {
        const batch = this.functionAreaMutationQueues.get(profileIndex)
        if (!batch?.length) break
        this.functionAreaMutationQueues.delete(profileIndex)

        const rejected = new Set<FunctionAreaMutation>()
        try {
          await this.runFunctionAreaSession(async (session) => {
            const area = await session.readFunctionAreaSnapshot()
            let hasValidMutation = false

            for (const mutation of batch) {
              // 每个更新先在副本上执行，防止一个非法更新污染同批其它字段。
              const candidate = area.slice()
              try {
                mutation.update(candidate)
                if (candidate.some((value, index) => value !== area[index])) {
                  area.set(candidate)
                  hasValidMutation = true
                }
              } catch (error) {
                rejected.add(mutation)
                mutation.reject(error)
              }
            }

            if (hasValidMutation) {
              await session.writeFunctionAreaSnapshot(area)
            }
          }, profileIndex)

          for (const mutation of batch) {
            if (!rejected.has(mutation)) mutation.resolve()
          }
        } catch (error) {
          for (const mutation of batch) {
            if (!rejected.has(mutation)) mutation.reject(error)
          }
        }
      }
    } finally {
      this.functionAreaMutationDrains.delete(profileIndex)
      if (this.functionAreaMutationQueues.get(profileIndex)?.length) {
        void this.drainFunctionAreaMutations(profileIndex)
      }
    }
  }

  private async readFunctionAreaSnapshotInChunks(
    channel: HidChannel,
    area: Uint8Array,
    profileBaseAddress: number,
  ): Promise<void> {
    let profileOffset = 0
    for (const length of FUNCTION_AREA_CHUNK_LENGTHS) {
      area.set(
        await this.readFunctionArea(channel, profileBaseAddress + profileOffset, length),
        profileOffset,
      )
      profileOffset += length
    }
  }

  private async writeFunctionAreaSnapshot(
    channel: HidChannel,
    area: Uint8Array,
    profileBaseAddress: number,
  ): Promise<void> {
    if (!(area instanceof Uint8Array) || area.byteLength !== FUNCTION_AREA_SNAPSHOT_LENGTH) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '功能区快照必须为 128 字节')
    }

    await this.writeFunctionAreaSnapshotInChunks(channel, area, profileBaseAddress)
  }

  private async writeFunctionAreaSnapshotInChunks(
    channel: HidChannel,
    area: Uint8Array,
    profileBaseAddress: number,
  ): Promise<void> {
    let profileOffset = 0
    for (const length of FUNCTION_AREA_CHUNK_LENGTHS) {
      await this.writeFunctionArea(
        channel,
        profileBaseAddress + profileOffset,
        area.slice(profileOffset, profileOffset + length),
      )
      profileOffset += length
    }
  }

  private async readFunctionArea(
    channel: HidChannel,
    address: number,
    length: number,
  ): Promise<Uint8Array> {
    const response = await this.requestCommand(
      channel,
      VISION_AP_MOUSE_COMMAND.getFunctionArea,
      length,
      address,
    )
    this.assertResponseLocation(response, address, length, '读取功能区')
    return response.data
  }

  private async writeFunctionArea(
    channel: HidChannel,
    address: number,
    data: Uint8Array,
  ): Promise<void> {
    const response = await this.requestCommand(
      channel,
      VISION_AP_MOUSE_COMMAND.setFunctionArea,
      data.byteLength,
      address,
      data,
    )
    this.assertResponseLocation(response, address, data.byteLength, '写入功能区')
  }

  private async readKeyArea(
    channel: HidChannel,
    address: number,
    length: number,
  ): Promise<Uint8Array> {
    const response = await this.requestCommand(
      channel,
      VISION_AP_MOUSE_COMMAND.getCurrentKeyArea,
      length,
      address,
    )
    this.assertResponseLocation(response, address, length, '读取当前按键区')
    return response.data
  }

  private async writeKeyArea(
    channel: HidChannel,
    address: number,
    data: Uint8Array,
  ): Promise<void> {
    const response = await this.requestCommand(
      channel,
      VISION_AP_MOUSE_COMMAND.setCurrentKeyArea,
      data.byteLength,
      address,
      data,
    )
    this.assertResponseLocation(response, address, data.byteLength, '写入当前按键区')
  }

  private async readMacroArea(
    channel: HidChannel,
    address: number,
    length: number,
  ): Promise<Uint8Array> {
    const response = await this.requestMacroReadCommand(channel, length, address)
    if (response.length === length && response.address === address) {
      return response.data
    }
    // AA 读取回包样本缺失；若设备未严格回显 LEN/ADDR，但 rawData 已包含宏区数据，则截取请求长度。
    if (response.rawData.byteLength >= length) {
      console.warn(`[TelinkMacro] AA 响应 LEN/ADDR 与请求不一致，使用 rawData 前 ${length} 字节`)
      return response.rawData.slice(0, length)
    }
    this.assertResponseLocation(response, address, length, '读取宏区')
    return response.data
  }

  private async requestMacroReadCommand(
    channel: HidChannel,
    length: number,
    address: number,
  ): Promise<VisionApResponse> {
    const command = VISION_AP_MOUSE_COMMAND.getMacroArea
    const payload = createVisionApPayload(command, length, address)
    const response = await this.requestCommandResponse(channel, payload, command, (input) =>
      isVisionApCommandEcho(input, command),
    )
    const parsed = parseVisionApResponse(response, command)
    if (parsed.ack !== 0) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `维盛 AP 命令 0x${command.toString(16)} 返回 ACK=0x${parsed.ack.toString(16).padStart(2, '0')}`,
      )
    }
    return parsed
  }

  private async writeMacroArea(
    channel: HidChannel,
    address: number,
    data: Uint8Array,
  ): Promise<void> {
    const response = await this.requestCommand(
      channel,
      VISION_AP_MOUSE_COMMAND.setMacroArea,
      data.byteLength,
      address,
      data,
    )
    this.assertResponseLocation(response, address, data.byteLength, '写入宏区')
  }

  /** V0106 名称区（profile / 宏）整区读取：全局单区，ADDR 从 0 起按分包连续寻址。 */
  private async readNameArea(
    channel: HidChannel,
    command: number,
    chunkLengths: readonly number[],
    byteLength: number,
    areaName: string,
  ): Promise<Uint8Array> {
    const area = new Uint8Array(byteLength)
    let address = 0
    for (const length of chunkLengths) {
      const response = await this.requestCommand(channel, command, length, address)
      this.assertResponseLocation(response, address, length, `读取${areaName}`)
      area.set(response.data, address)
      address += length
    }
    return area
  }

  /** V0106 名称区整区写入：协议要求即使只改一个名称也必须整区写满。 */
  private async writeNameArea(
    channel: HidChannel,
    command: number,
    chunkLengths: readonly number[],
    byteLength: number,
    area: Uint8Array,
    areaName: string,
  ): Promise<void> {
    if (!(area instanceof Uint8Array) || area.byteLength !== byteLength) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${areaName}必须为 ${byteLength} 字节`)
    }
    let address = 0
    for (const length of chunkLengths) {
      const data = area.slice(address, address + length)
      const response = await this.requestCommand(channel, command, data.byteLength, address, data)
      this.assertResponseLocation(response, address, data.byteLength, `写入${areaName}`)
      address += length
    }
  }

  private async requestCommand(
    channel: HidChannel,
    command: number,
    length = 0,
    address = 0,
    data?: Uint8Array,
  ): Promise<VisionApResponse> {
    const payload = createVisionApPayload(command, length, address, data)
    const response = await this.requestCommandResponse(channel, payload, command)
    const parsed = parseVisionApResponse(response, command)
    if (parsed.ack !== 0) {
      const code = VISION_AP_READ_COMMANDS.has(command)
        ? DeviceErrorCode.READ_FAILED
        : DeviceErrorCode.WRITE_FAILED
      throw new DeviceError(
        code,
        `维盛 AP 命令 0x${command.toString(16)} 返回 ACK=0x${parsed.ack.toString(16).padStart(2, '0')}`,
      )
    }
    return parsed
  }

  /**
   * 鼠标休眠时会吞掉首个读取命令；先用短等待触发唤醒，再仅重放一次原请求。
   * 仅只读命令（A4/A7/AA/AD/BD/B9/BB）参与重试；A5/A8/AB/BA/BC 写入命令与 A9 绝不重放。
   */
  private async requestCommandResponse(
    channel: HidChannel,
    payload: Uint8Array,
    command: number,
    validate?: (input: DataView) => boolean,
  ): Promise<DataView> {
    if (!WAKE_RETRYABLE_COMMANDS.has(command)) {
      return this.exchangeInputReport(channel, payload, COMMAND_RESPONSE_TIMEOUT_MS, validate)
    }

    try {
      return await this.exchangeInputReport(channel, payload, WAKE_PROBE_TIMEOUT_MS, validate)
    } catch (error) {
      if (!(error instanceof DeviceError) || error.code !== DeviceErrorCode.TIMEOUT) {
        throw error
      }

      await delay(WAKE_RETRY_DELAY_MS)
      try {
        return await this.exchangeInputReport(
          channel,
          payload,
          COMMAND_RESPONSE_TIMEOUT_MS,
          validate,
        )
      } catch (retryError) {
        if (retryError instanceof DeviceError && retryError.code === DeviceErrorCode.TIMEOUT) {
          throw new DeviceError(
            DeviceErrorCode.TIMEOUT,
            `${this.getWakeRetryOperationName(command)}在唤醒重试后仍未响应；请移动或点击鼠标后重试`,
            retryError,
          )
        }
        throw retryError
      }
    }
  }

  private async exchangeInputReport(
    channel: HidChannel,
    payload: Uint8Array,
    timeout: number,
    validate?: (input: DataView) => boolean,
  ): Promise<DataView> {
    const exchange = new InputReportExchange(channel)
    return exchange.request({
      reportId: VISION_AP_MOUSE_HID.controlReportId,
      payload,
      responseReportId: VISION_AP_MOUSE_HID.controlReportId,
      timeout,
      validate: validate ?? ((input) => isVisionApResponseForRequest(input, payload)),
    })
  }

  private getWakeRetryOperationName(command: number): string {
    if (command === VISION_AP_MOUSE_COMMAND.getBatteryLevel) {
      return '电量查询'
    }
    if (command === VISION_AP_MOUSE_COMMAND.readBatteryRuntime) {
      return '续航查询'
    }
    return `维盛 AP 读取命令 0x${command.toString(16)}`
  }

  /** 协议明确标记为不回包的命令必须直发，不能进入 InputReportExchange 的三秒等待。 */
  private async sendCommandWithoutResponse(
    channel: HidChannel,
    command: number,
    length = 0,
    address = 0,
    data?: Uint8Array,
  ): Promise<void> {
    await channel.sendOutputReport(
      VISION_AP_MOUSE_HID.controlReportId,
      createVisionApPayload(command, length, address, data),
    )
  }

  /** 配对进窗：Cs 固定 00 00，与 Qt InterruptReportProtocol 一致。 */
  private async sendPairingCommandWithoutResponse(
    channel: HidChannel,
    command: number,
  ): Promise<void> {
    await channel.sendOutputReport(
      VISION_AP_MOUSE_HID.controlReportId,
      createVisionApPairingPayload(command),
    )
  }

  private assertResponseLocation(
    response: VisionApResponse,
    address: number,
    length: number,
    action: string,
  ): void {
    if (response.address !== address || response.length !== length) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, `${action}响应的 LEN 或 ADDR 不匹配`)
    }
  }

  /**
   * 检测连接类型 (有线/无线)，与 Nordic 鼠标模型一致，全部从 manifest 读取：
   * 1. productId 中声明为无线的 PID（奇数索引，有线/无线成对）
   * 2. 设备名称包含 "Receiver" / "Dongle"
   */
  private isWirelessConnection(channel: HidChannel): boolean {
    const deviceInfo = channel.deviceInfo
    if (!deviceInfo) return false
    if (this.isManifestWirelessProductId(deviceInfo.productId)) return true
    return (
      typeof deviceInfo.productName === 'string' && /Receiver|Dongle/i.test(deviceInfo.productName)
    )
  }

  private isManifestWirelessProductId(productId: number | undefined): boolean {
    if (typeof productId !== 'number' || !Number.isInteger(productId)) return false

    const productIds = Array.isArray(this.manifest.hardware.productId)
      ? this.manifest.hardware.productId
      : [this.manifest.hardware.productId]
    return productIds.some((candidate, index) => index % 2 === 1 && candidate === productId)
  }
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds))
}
